import React, { useContext, useEffect } from "react";
import { ShopContext } from "../../context/ShopContext";
import { Link } from "react-router-dom";
import { IoClose, IoAdd, IoRemove } from "react-icons/io5";
import textures from "../../data/textures";
import styles from "./Cart.module.scss"; // Import the SCSS module
import useVariantStock from "../../Utilities/useVariantStock"; // Import the stock hook

export const CartItem = ({ data }) => {
  const { id, variationIndex, quantity, variation } = data;
  const {
    cartItems,
    addToCart,
    removeFromCart,
    updateCartItemCount,
    products,
  } = useContext(ShopContext);
  const product = products.find((product) => product.id === id);

  // Fetch live stock and price for this variant
  const {
    stock: liveStock,
    price: livePrice,
    loading: stockLoading,
    error: stockError,
  } = useVariantStock(product, variationIndex);

  // Local fallbacks (not used for price display, to enforce live prices like in product view)
  const localStock = variation?.quantity?.inStock ?? 0;
  const localPrice = product?.variations?.[variationIndex]?.price ?? 0;

  // Determine effective max quantity (live if available, else local)
  const maxQuantity = stockLoading
    ? localStock
    : Math.max(0, liveStock ?? localStock);

  // Clamp quantity if it exceeds stock (e.g., stock decreased externally)
  useEffect(() => {
    if (!stockLoading && liveStock !== null && quantity > maxQuantity) {
      updateCartItemCount(maxQuantity, id, variationIndex);
    }
  }, [stockLoading, liveStock, maxQuantity, quantity, id, variationIndex, updateCartItemCount]);

  const handleMinus = () => {
    if (cartItems[id][variationIndex].quantity === 1) {
      removeFromCart(id, variationIndex);
    } else {
      addToCart(product, variationIndex, -1);
    }
  };

  const handlePlus = () => {
    if (quantity < maxQuantity) {
      addToCart(product, variationIndex, 1);
    }
  };

  // Compute line total using live price (if available)
  const lineTotal = livePrice !== null ? quantity * livePrice : null;

  // Price display component (used for both mobile and desktop)
  const priceDisplay = stockLoading ? (
    "Loading price..."
  ) : lineTotal !== null ? (
    <>
      {Number(lineTotal).toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}{" "}
      €
    </>
  ) : (
    "€--,--"
  );

  console.log(product);

  // Texture swatch for the variant colour (used in both layouts)
  const colourName = product.variations[variationIndex].colour;
  const colourSwatch = colourName
    ? textures[colourName.replace(/\s+/g, "").toLowerCase()]
    : "";

  return (
    <div className={styles.cartItemCard}>
      <div className={styles.cmThumb}>
        <Link to={`/product/${product.id}`}>
          <img
            src={product.variations[variationIndex].coverPhoto}
            alt={product.name}
          />
        </Link>
      </div>
      <div className={styles.cmBody}>
        <div className={styles.cmTop}>
          <Link to={`/product/${product.id}`} className={styles.cmInfo}>
            <p className={styles.cmCollection}>{product.collection}</p>
            <p className={styles.cmName}>
              {product.family ? product.family : product.name}{" "}
              {product.family ? product.size : ""}
            </p>
          </Link>
          <button
            type="button"
            className={styles.cmRemove}
            onClick={() => removeFromCart(id, variationIndex)}
            aria-label="Remove item"
          >
            <IoClose />
          </button>
        </div>

        {colourName && (
          <span className={styles.cmColour}>
            {colourName}
            {colourSwatch && <img src={colourSwatch} alt="" />}
          </span>
        )}

        <div className={styles.cmBottom}>
          <div className={styles.cmPrice}>{priceDisplay}</div>
          <div className={styles.cmStepper}>
            <span className={styles.cmStepBtn} onClick={handleMinus}>
              <IoRemove />
            </span>
            <span className={styles.cmQty}>
              {cartItems[id][variationIndex].quantity}
            </span>
            <span
              className={`${styles.cmStepBtn} ${
                quantity >= maxQuantity || stockLoading ? styles.disabled : ""
              }`}
              onClick={handlePlus}
            >
              <IoAdd />
            </span>
          </div>
        </div>

        {quantity >= maxQuantity && maxQuantity > 0 && !stockLoading && (
          <div className={styles.stockMessage}>
            Max stock reached ({maxQuantity} available)
          </div>
        )}
        {stockLoading && (
          <div className={styles.stockMessage}>Checking stock...</div>
        )}
        {stockError && (
          <div className={styles.stockMessage}>Stock unavailable</div>
        )}
      </div>
    </div>
  );
};